import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [setup, setSetup] = useState(null); // { secret, otpauth_url }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    try { await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password changed"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const load = () => api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    try { const { data } = await api.post("/admin/2fa/setup"); setSetup(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const enable = async () => {
    setBusy(true);
    try { await api.post("/admin/2fa/enable", { code }); toast.success("2FA enabled"); setSetup(null); setCode(""); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    const c = window.prompt("Enter a current 2FA code to disable two-factor authentication:");
    if (!c) return;
    try { await api.post("/admin/2fa/disable", { code: c }); toast.success("2FA disabled"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Settings" subtitle="Platform configuration and account security">
      {!settings ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6" data-testid="admin-settings">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-border bg-card p-5 space-y-2">
              <h3 className="text-sm font-bold">Platform</h3>
              <Row label="Environment" value={settings.environment} />
              <Row label="Owner" value={settings.owner?.email || "not bootstrapped"} />
              <Row label="Owner bootstrapped" value={settings.owner_bootstrapped ? "yes" : "no"} />
              <Row label="Email sender" value={settings.email?.from_name} />
              <Row label="Reply-to" value={settings.email?.reply_to} />
              <Row label="Email configured" value={settings.email?.configured ? "yes" : "no"} />
            </div>

            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-bold mb-2">Two-factor authentication</h3>
              <p className="text-sm text-muted-foreground mb-3">{settings.your_2fa_enabled ? "2FA is currently enabled on your account." : "Add an authenticator app for a second login factor. Recommended for all admins and required for the Owner."}</p>
              {settings.your_2fa_enabled ? (
                <button data-testid="disable-2fa" onClick={disable} className="rounded-xl border border-red-500/40 text-red-500 px-4 py-2 text-sm font-semibold hover:bg-red-500/10">Disable 2FA</button>
              ) : setup ? (
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">Add this secret to your authenticator app (Google Authenticator, Authy, 1Password):</div>
                  <code data-testid="totp-secret" className="block break-all rounded-lg bg-muted px-3 py-2 text-sm font-mono">{setup.secret}</code>
                  <input data-testid="totp-code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter 6-digit code" className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40 tracking-widest" />
                  <button data-testid="enable-2fa" onClick={enable} disabled={busy} className="w-full rounded-xl bg-primary text-primary-foreground py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60">{busy ? "Verifying..." : "Verify & enable"}</button>
                </div>
              ) : (
                <button data-testid="setup-2fa" onClick={startSetup} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold hover:opacity-90">Set up 2FA</button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5" data-testid="admin-change-password">
            <h3 className="text-sm font-bold mb-1 flex items-center gap-2"><Icon name="KeyRound" className="h-4 w-4" />Change your password</h3>
            <p className="text-sm text-muted-foreground mb-3">Updating your password signs out your other active sessions. <span className="font-medium">Signed in with Google?</span> Leave “Current password” blank to set one for the first time.</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><label className="text-xs font-semibold text-muted-foreground">Current password</label><input data-testid="admin-cp-current" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="inp" /></div>
              <div><label className="text-xs font-semibold text-muted-foreground">New password</label><input data-testid="admin-cp-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="inp" /></div>
              <div><label className="text-xs font-semibold text-muted-foreground">Confirm new</label><input data-testid="admin-cp-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="inp" /></div>
            </div>
            <button data-testid="admin-cp-submit" onClick={changePassword} className="mt-3 rounded-xl bg-primary text-primary-foreground px-5 py-2 text-sm font-bold hover:opacity-90">Update password</button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="text-sm font-bold mb-3">Administrators</h3>
            <div className="space-y-1">
              {(settings.admins || []).map((a) => (
                <div key={a.email} className="flex items-center justify-between text-sm py-1.5 border-b border-border/60 last:border-0">
                  <span className="font-medium">{a.email} {a.is_test_admin && <span className="text-xs text-muted-foreground">(test)</span>}</span>
                  <span className="flex items-center gap-2 text-xs">
                    <span className="capitalize px-2 py-0.5 rounded-full bg-primary/10 text-primary">{a.platform_role}</span>
                    {a.totp_enabled && <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">2FA</span>}
                    {a.disabled && <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground">disabled</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value || "—"}</span></div>
);
